import { IconChevronLeft, IconChevronRight } from "@tabler/icons-react";

type Props = {
  selectOptions?: any;
  name?: string;
  opt?: string;
  totalPages?: number;
  currentPage?: number; // kept optional for safety
  fetchData?: (pageNo: number) => void;
  totalItems?: number; // optional
  totalCount?: number; // optional
  itemLabel?: string;  // optional
};

export default function Pagination(props: Props) {
  const { fetchData, totalPages = 5, currentPage = 1, totalItems, totalCount, itemLabel = "items" } = props;
  const finalTotal = totalCount !== undefined ? totalCount : totalItems;

  const handlePageNo = (pageNo: number) => {
    if (pageNo < 1 || pageNo > totalPages) return;
    fetchData?.(pageNo);
  };

  const renderPageNumbers = () => {
    const pages = [];
    const start = Math.max(2, currentPage - 1);
    const end = Math.min(totalPages - 1, currentPage + 1);

    // Always show first page
    pages.push(
      <button
        key={1}
        className={`w-8 h-8 flex items-center justify-center rounded-lg border text-xs font-bold transition shadow-sm cursor-pointer ${
          currentPage === 1
            ? "bg-[#FBD037] border-[#FBD037] text-[#131327]"
            : "bg-white border-slate-200 text-slate-600 hover:bg-slate-50"
        }`}
        onClick={() => handlePageNo(1)}
      >
        1
      </button>
    );

    if (start > 2) {
      pages.push(
        <span key="start-ellipsis" className="w-8 h-8 flex items-center justify-center text-xs text-slate-400 font-bold">
          …
        </span>
      );
    }

    for (let i = start; i <= end; i++) {
      pages.push(
        <button
          key={i}
          className={`w-8 h-8 flex items-center justify-center rounded-lg border text-xs font-bold transition shadow-sm cursor-pointer ${
            currentPage === i
              ? "bg-[#FBD037] border-[#FBD037] text-[#131327]"
              : "bg-white border-slate-200 text-slate-600 hover:bg-slate-50"
          }`}
          onClick={() => handlePageNo(i)}
        >
          {i}
        </button>
      );
    }

    if (end < totalPages - 1) {
      pages.push(
        <span key="end-ellipsis" className="w-8 h-8 flex items-center justify-center text-xs text-slate-400 font-bold">
          …
        </span>
      );
    }

    if (totalPages > 1) {
      pages.push(
        <button
          key={totalPages}
          className={`w-8 h-8 flex items-center justify-center rounded-lg border text-xs font-bold transition shadow-sm cursor-pointer ${
            currentPage === totalPages
              ? "bg-[#FBD037] border-[#FBD037] text-[#131327]"
              : "bg-white border-slate-200 text-slate-600 hover:bg-slate-50"
          }`}
          onClick={() => handlePageNo(totalPages)}
        >
          {totalPages}
        </button>
      );
    }

    return pages;
  };

  if (totalPages <= 0) return null;

  return (
    <div className={`flex items-center w-full px-2 ${finalTotal !== undefined ? "justify-between" : "justify-end"}`}>
      {finalTotal !== undefined && (
        <p className="text-xs text-slate-500 font-semibold select-none">
          Page <span className="text-slate-800 font-black">{currentPage}</span> of{" "}
          <span className="text-slate-800 font-black">{totalPages}</span>
        </p>
      )}
      <div className="flex items-center gap-1">
        <button
          onClick={() => handlePageNo(currentPage - 1)}
          disabled={currentPage === 1}
          className="flex items-center justify-center w-8 h-8 rounded-lg border border-slate-200 bg-white text-slate-600 hover:bg-slate-50 disabled:opacity-30 disabled:cursor-not-allowed transition shadow-sm cursor-pointer"
        >
          <IconChevronLeft size={14} stroke={2.5} />
        </button>

        {/* Page numbers */}
        {renderPageNumbers()}

        {/* Next */}
        <button
          onClick={() => handlePageNo(currentPage + 1)}
          disabled={currentPage === totalPages}
          className="flex items-center justify-center w-8 h-8 rounded-lg border border-slate-200 bg-white text-slate-600 hover:bg-slate-50 disabled:opacity-30 disabled:cursor-not-allowed transition shadow-sm cursor-pointer"
        >
          <IconChevronRight size={14} stroke={2.5} />
        </button>
      </div>
    </div>
  );
}